import React, { useState, useContext } from 'react';

import classNames from 'classnames';

import Button from 'CommonComponents/Button';

import ProjectRoadmapContext from 'Context/project_plan/ProjectRoadmapContext';

import DownloadIcon from 'Assets/icons/download.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

const SCALING_OPTIONS = [
    {
        value: 'fit',
        label: 'Fit to one page',
        description:
            'Auto-scale to fit all columns on one landscape page (A4, or A3 when the timeline is too wide for A4). Uses milestone codes and a legend when text would be too small to read.',
    },
    {
        value: 'multi-page',
        label: 'Multi-page',
        description: 'Break at category boundaries across multiple A4 landscape pages. Full milestone text wraps inside each pill.',
    },
    {
        value: 'actual',
        label: 'Actual size',
        description: 'Render at on-screen scale and paginate as needed. Full milestone text wraps inside each pill.',
    },
];

export default function RoadmapPdfModal({ onClose }) {
    const { downloadPdf, pdfDownloading, lastPdfScaling } = useContext(ProjectRoadmapContext);
    const [selectedScaling, setSelectedScaling] = useState(lastPdfScaling || 'multi-page');

    const handleDownload = async () => {
        await downloadPdf(selectedScaling);
        onClose();
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-charcoal tw-bg-opacity-40" onClick={onClose}>
            <div
                className="tw-bg-white tw-rounded-xl tw-shadow-primary tw-border tw-border-grey tw-p-6 tw-w-[460px] tw-max-h-[90vh] tw-overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
                    <h2 className="tw-text-base tw-font-semibold tw-text-charcoal">{$t('Download PDF')}</h2>
                    <button onClick={onClose} className="tw-text-charcoal-50 hover:tw-text-charcoal" aria-label={$t('Close')}>
                        <XMarkIcon className="tw-h-5 tw-w-5" />
                    </button>
                </div>

                {/* Scaling options */}
                <div className="tw-mb-6 tw-space-y-3">
                    <div className="tw-text-xs tw-font-semibold tw-text-charcoal tw-uppercase tw-tracking-wide tw-mb-2">{$t('Page scaling')}</div>
                    {SCALING_OPTIONS.map((option) => (
                        <label
                            key={option.value}
                            className={classNames('tw-flex tw-items-start tw-gap-x-3 tw-p-3 tw-rounded-lg tw-border tw-cursor-pointer tw-transition-colors', {
                                'tw-border-blue tw-bg-blue-10': selectedScaling === option.value,
                                'tw-border-grey hover:tw-border-charcoal-30': selectedScaling !== option.value,
                            })}
                        >
                            <input
                                type="radio"
                                name="pdf-scaling"
                                value={option.value}
                                checked={selectedScaling === option.value}
                                onChange={() => setSelectedScaling(option.value)}
                                className="tw-mt-0.5 tw-shrink-0"
                            />
                            <div>
                                <div className="tw-text-sm tw-font-medium tw-text-charcoal">{$t(option.label)}</div>
                                <div className="tw-text-xs tw-text-charcoal-50 tw-mt-0.5 tw-leading-relaxed">{$t(option.description)}</div>
                            </div>
                        </label>
                    ))}
                </div>

                {/* Actions */}
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-3">
                    <button onClick={onClose} className="tw-text-sm tw-text-charcoal-50 hover:tw-text-charcoal tw-font-medium" disabled={pdfDownloading}>
                        {$t('Cancel')}
                    </button>
                    <Button variant="primary" small onClick={handleDownload} loading={pdfDownloading} disabled={pdfDownloading}>
                        <DownloadIcon className="tw-h-4 tw-w-4 tw-me-1.5" />
                        {pdfDownloading ? $t('Generating…') : $t('Download PDF')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
